import React from 'react';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';

import { Collapsible } from 'components/shared/Collapsible';
import { Icon } from 'components/shared/Icon';
import { Typography } from 'components/shared/Typography';

import { StatusTag } from 'components/StatusTag';

import {
  EXIT_CAUSE_ICONS,
  EXIT_CAUSE_TAG_VARIANTS,
  getExitCauseLabel,
  type ExitCause,
} from '../../TimelineSection.helpers';

import styles from './ExitRules.module.scss';

/** Same priority order as the admin-api classifier (§3.3.9). */
const RULES_ORDER: ExitCause[] = ['error', 'stuck', 'idle', 'goal', 'left'];

const COLLAPSIBLE_TRANSITION_MS = 150;
const TOGGLE_BUTTON_PROPS = { type: 'button' };

const renderToggle = (iconName: string) => (
  <span className={styles.toggle}>
    <Typography
      className={styles.toggleText}
      component="span"
      variant="body1"
      weight="semiBold"
      mode="compact"
    >
      {getTranslation('student_page.journey.rules.title')}
    </Typography>
    <Icon className={styles.toggleIcon} name={iconName} />
  </span>
);

/**
 * «Как считаются причины?» — the rules behind the exit causes, collapsed by
 * default: admins read them once, not on every student page.
 */
const ExitRules = () => {
  useTranslation();

  return (
    <section className={styles.panel}>
      {/* No `lazyRender`: Collapsible measures scrollHeight before lazy children mount. */}
      <Collapsible
        transitionTime={COLLAPSIBLE_TRANSITION_MS}
        triggerTagName="button"
        triggerClassName={styles.toggleButton}
        triggerOpenedClassName={styles.toggleButton}
        triggerElementProps={TOGGLE_BUTTON_PROPS}
        trigger={renderToggle('chevronDown')}
        triggerWhenOpen={renderToggle('chevronUp')}
      >
        <div className={styles.rules}>
          {RULES_ORDER.map((cause) => (
            <div key={cause} className={styles.rule}>
              <StatusTag
                className={styles.badge}
                variant={EXIT_CAUSE_TAG_VARIANTS[cause]}
                icon={EXIT_CAUSE_ICONS[cause]}
              >
                {getExitCauseLabel(cause)}
              </StatusTag>
              <Typography className={styles.text} variant="body3">
                {getTranslation(`student_page.journey.rules.${cause}`)}
              </Typography>
            </div>
          ))}
        </div>
      </Collapsible>
    </section>
  );
};

export default React.memo(ExitRules) as any;
